import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import shallow from "zustand/shallow";

import _ from "lodash";
import FlipMove from "react-flip-move";

import { sortUsersByActivity } from "js/utils/userUtils";
import { arrayMove } from "js/utils/arrayUtils";
import * as windowUtils from "js/utils/windowUtils";
import p2pManager from "js/utils/p2pManager";

import ReportsDropdown from "./ReportsDropdown";
import Nav from "./Nav";
import EndClassButton from "./EndClassButton";
import OneActiveMonitorWarningModal from "./OneActiveMonitorWarningModal";
import iconModifyStudents from "assets/IconModifyStudents.svg?url";
import ClassroomStartSession from "./ClassroomStartSession";
import MonitoringClassroomUser from "./MonitoringClassroomUser";
import { windowLimit } from "./RegularClassroom";
import P2PDisplayModal from "./P2PDisplayModal";
import AgentP2PModal from "./P2PDisplayModal/AgentP2PModal";
import ClassroomEditSession from "js/components/ClassroomEditSession";
import EditClassButton from "./EditClassButton";
import StartMonitoringButton from "./StartMonitoringButton";
import { findUnixExpiryFromPeriods, generateClassExpiryStr } from "js/utils/timeUtils";
import { TabBar } from "./TabBar";
import { FocusAndFlexibleGridBar } from "./RegularClassroom/FocusAndFlexibleGridBar";

import useStore from "stores/index";
import { getClassroomStore } from "stores/classroomStore";
import { getSignatureDb } from "stores/signatureStore";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { useClassroomSessionStore } from "stores/v2/classroomSessionStore";
import { useClassroomPolicyStore } from "stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { ModifyStudentsModal } from "@/components/ModifyStudentsModal";
import { useDisclosure } from "@chakra-ui/react";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const MonitoringClassroom = (props) => {
	const store = useStore.getState();

	const [activeClassroom] = useState(useUserSessionStore.getState().getActiveGroup());
	const [view, setView] = useState("screens"); // enum of ('screens', 'tabs')
	const [showAllWindows, setShowAllWindows] = useState(false);
	const [classroomPolicy, setClassroomPolicy] = useState(useClassroomPolicyStore.getState());
	const [classExpiryStr, setClassExpiryStr] = useState();
	const [signatureDb] = useState(getSignatureDb(store));
	const [studentSortOrder, setStudentSortOrder] = useState("firstNameAscending"); // enum of ('firstNameAscending', 'lastNameAscending')

	// modals
	const modifyStudentsModal = useDisclosure();
	const [showStartClassModal, setShowStartClassModal] = useState(false);
	const [showEditClassModal, setShowEditClassModal] = useState(false);
	const [showMonitoringClassActiveWarning, setShowMonitoringClassActiveWarning] = useState(false);
	const [p2pModalOpen, setP2pModalOpen] = useState(false);
	// errors
	const [startClassSessionError, setStartClassSessionError] = useState(false);

	const [p2pModalUser, setP2pModalUser] = useState();
	const [p2pAgentId, setP2pAgentId] = useState();
	const [p2pChromeWindowId, setP2pChromeWindowId] = useState();
	const [p2pModalSelectedTab, setP2pModalSelectedTab] = useState();
	const [p2pModalFollowActiveTab, setP2pModalFollowActiveTab] = useState(false);

	const [users, setUsers] = useState(props.users);
	const [classroom, setClassroom] = useState(props.classroom);

	const [tabItemLimit, setTabItemLimit] = React.useState(5); // variable tab items to display on student tile

	const selectedClassroomStore = useSelectedClassroomStore();

	useEffect(() => {
		setUsers(props.users);
		setClassroom(props.classroom);
		setStudentSortOrder(props.classroom?.personalisation?.studentSortOrder ?? "firstNameAscending");
		setClassExpiryStr(generateSessionStatusTxt());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props]);

	useEffect(() => {
		_.debounce((users) => {
			p2pManager.checkUsersToFetchToken(users);
		}, 3000);
	}, [users]);

	useEffect(() => {
		setClassExpiryStr(generateSessionStatusTxt());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isClassStarted]);

	// didMount
	useEffect(() => {
		const onChangeClassroomPolicy = (state) => setClassroomPolicy(state);

		const onChangeClassroomSessionStore = (state) => {
			setShowStartClassModal(state.showStartClassModal);
			setShowEditClassModal(state.showEditClassModal);
			setStartClassSessionError(state.startClassSessionError);
			setClassExpiryStr(state.classExpiryStr);
			setShowMonitoringClassActiveWarning(state.showMonitoringClassActiveWarning);
		};

		const unsubFromClassroomSessionStore = useClassroomSessionStore.subscribe(
			(state) => state,
			(newState) => {
				onChangeClassroomSessionStore(newState);
			}
		);

		const unsubClassroomPolicyStore = useClassroomPolicyStore.subscribe(
			(state) => state,
			(newState) => onChangeClassroomPolicy(newState)
		);

		return () => {
			unsubClassroomPolicyStore();
			unsubFromClassroomSessionStore();
		};
	}, []);

	const toggleShowWindows = () => setShowAllWindows(!showAllWindows);

	const classroomStore = useStore(
		(state) => ({
			setStudentSortOrder: state.classroom.setStudentSortOrder,
		}),
		shallow
	);

	const onSortOrderChange = (sortOrder) => {
		setStudentSortOrder(sortOrder);
		classroomStore.setStudentSortOrder(classroom.id, sortOrder);
	};

	const openP2pModal = (user, tabId, peerAgentId, chromeWindowId, followActiveTab = false) => {
		setP2pModalUser(user.username);
		setP2pModalSelectedTab(tabId);
		setP2pAgentId(peerAgentId);
		setP2pChromeWindowId(chromeWindowId);
		setP2pModalOpen(true);
		setP2pModalFollowActiveTab(followActiveTab);
	};

	const closeP2pModal = () => {
		setP2pModalOpen(false);
	};

	const renderP2pModal = () => {
		const user = users.find((user) => user.username === p2pModalUser);
		const agent = user.screenshots.find((screen) => screen.chrome_id === p2pAgentId && screen.chrome_window_id === p2pChromeWindowId);
		const platform = windowUtils.getPlatformType(agent);
		const chrome = platform === "Chromebook";

		if (!agent || !user || (!p2pModalSelectedTab && chrome)) return;
		if (!chrome && !store.featureToggles.data["agent-p2p-screenshots"]) return;

		if (chrome) {
			const tabs = [
				{
					tab_id: agent.tab_id,
					favIcon: agent.favicon,
					tabUrl: agent.url,
					title: agent.title,
				},
				...agent.background_tabs,
			];
			const startTabIndex = tabs.findIndex((tab) => tab.tab_id === p2pModalSelectedTab);

			return (
				<P2PDisplayModal
					isMonitoringClass={true}
					open={p2pModalOpen}
					close={closeP2pModal}
					user={user}
					startTab={tabs[startTabIndex]}
					windowId={p2pChromeWindowId}
					startTabIndex={startTabIndex}
					peerAgentId={p2pAgentId}
					activeTab={agent}
					tabs={tabs}
					classroom={classroom}
					followActiveTab={p2pModalFollowActiveTab}
					platform={platform}
				/>
			);
		} else {
			return (
				<AgentP2PModal
					isMonitoringClass={true}
					open={p2pModalOpen}
					close={closeP2pModal}
					user={user}
					activeTab={agent}
					windowId={p2pChromeWindowId}
					peerAgentId={p2pAgentId}
					classroom={classroom}
					platform={platform}
				/>
			);
		}
	};

	let totalWindows = 0;
	let shownWindows = 0;
	let extraWindows = 0;
	const isClassActive = getClassroomStore(store).isClassSessionActiveV2(classroom.id);
	let sortedUsers = sortUsersByActivity(users, classroomPolicy.locked_users, studentSortOrder === "lastNameAscending");
	sortedUsers = selectedClassroomStore.sortUsersByPin(sortedUsers);

	let elements = [];
	for (const user of sortedUsers) {
		let ret = [];
		const uniqueDevices = Array.from(new Set(user.screenshots.map((windowItem) => windowItem.chrome_id))).sort();
		for (const windowData of user.screenshots) {
			ret.push(
				<MonitoringClassroomUser
					key={user.username + "_" + windowData.chrome_id + "_" + windowData.chrome_window_id}
					user={user}
					view={view}
					deviceIndex={uniqueDevices.indexOf(windowData.chrome_id) + 1}
					windowData={windowData}
					signatureDb={signatureDb}
					isClassActive={isClassActive}
					classroom={classroom}
					openP2pModal={openP2pModal}
					tabItemLimit={tabItemLimit}
				/>
			);
		}

		if (ret.length === 0) {
			ret.push(
				<MonitoringClassroomUser
					key={user.username}
					user={user}
					view={view}
					windowData={{}}
					signatureDb={signatureDb}
					isClassActive={isClassActive}
					classroom={classroom}
					tabItemLimit={tabItemLimit}
				/>
			);
		}
		const knownDevices = [];
		let lastUniqueDeviceIndex = 0;
		for (const screenIndex in ret) {
			const currentScreen = ret[screenIndex];
			if (currentScreen.props.deviceIndex && knownDevices.indexOf(currentScreen.props.deviceIndex) < 0) {
				knownDevices.push(currentScreen.props.deviceIndex);
				arrayMove(ret, screenIndex, lastUniqueDeviceIndex);
				lastUniqueDeviceIndex++;
			}
		}
		totalWindows += ret.length;
		if (!showAllWindows && ret.length >= windowLimit) {
			ret = ret.slice(0, windowLimit);
		}
		if (showAllWindows && ret.length >= windowLimit) {
			extraWindows += ret.length - windowLimit;
		}
		shownWindows += ret.length;

		elements = [...elements, ...ret];
	}

	// Disable pin animations for the first 500ms to prevent unnecessary animations on page rerender
	const [pinAnimationsEnabled, setPinAnimationsEnabled] = useState(false);
	useEffect(() => {
		setTimeout(() => {
			setPinAnimationsEnabled(true);
		}, 500);
	}, []);

	// TODO: investigate why array of string is returned but not a string?
	const generateSessionStatusTxt = (verbose = false) => {
		const classroomData = getClassroomStore(store).getClassroomV2(classroom.id);
		const sessionEndTime = classroomData.session?.sessionEndTime;
		const isMonitoring = classroomData.session?.isMonitoring;
		if (isMonitoring) {
			if (sessionEndTime && sessionEndTime > new Date().getTime() / 1000) {
				return [generateClassExpiryStr(sessionEndTime, verbose, isMonitoring)];
			}
			return [false];
		} else {
			if (classroomData.schedule.active_until > new Date().getTime() / 1000) {
				return [generateClassExpiryStr(classroomData.schedule.active_until, verbose, isMonitoring === true)];
			} else {
				const expiryUnix = findUnixExpiryFromPeriods(classroomData.schedule.periods);
				return [generateClassExpiryStr(expiryUnix, verbose, isMonitoring === true)];
			}
		}
	};

	return (
		<div>
			<Nav selectedClass={classroom} />
			<TabBar />
			<ul className="ctxbar--classroomPage">
				<li
					className={isClassActive ? (view === "screens" ? "ctxbar-toggle--active" : "ctxbar-toggle") : "ctxbar-toggle-hide"}
					onClick={() => setView("screens")}
				>
					Screen view
				</li>
				<li
					className={isClassActive ? (view === "tabs" ? "ctxbar-toggle--active--list" : "ctxbar-toggle--list") : "ctxbar-toggle-hide"}
					onClick={() => setView("tabs")}
				>
					List view
				</li>
				<li>
					{isClassActive ? (
						<EditClassButton
							classroomName={activeClassroom}
							classExpiryStr={classExpiryStr}
							scheduledClass={false}
							isMonitoringClass={true}
							screenView={true}
							classActive={isClassActive}
							classroomId={classroom.id}
						>
							Monitoring until {classExpiryStr}
						</EditClassButton>
					) : (
						<StartMonitoringButton classroomName={activeClassroom} monitoring={true} />
					)}
				</li>

				{isClassActive && (
					<li>
						<EndClassButton classroomName={activeClassroom} isMonitoringClass={true} />
					</li>
				)}

				<ul className="ctxbar_alignRight">
					<ReportsDropdown students={users} />
					<div
						title="Modify Students"
						className="ctxbar_button MonitoringClassroom_modifyStudentsBtn"
						onClick={() => modifyStudentsModal.onOpen()}
					>
						<img src={iconModifyStudents} width="12" alt="" />
						<span>Modify Students</span>
					</div>
				</ul>
			</ul>
			{isClassActive && <div className="MonitoringClassroom_monitorOnlyBanner">Monitor only mode</div>}

			<FocusAndFlexibleGridBar
				setTabItemLimit={setTabItemLimit}
				appliedFocuses={[]}
				isUpdatingFocus={false}
				endFocus={() => {}}
				studentSortOrder={studentSortOrder}
				onSortOrderChange={onSortOrderChange}
				bubbleUpdatedFocus={() => {}}
			/>

			{/*Pinning animation*/}
			<FlipMove
				className="RegularClassroom_students"
				disableAllAnimations={!pinAnimationsEnabled}
				data-testid="flip-move"
				data-test-active={pinAnimationsEnabled}
			>
				{elements}
			</FlipMove>
			<ModifyStudentsModal
				isOpen={modifyStudentsModal.isOpen}
				onClose={modifyStudentsModal.onClose}
				classroom={classroom}
				students={users}
				isMonitoringClass={true}
			/>
			<ClassroomStartSession
				open={showStartClassModal}
				onStartError={startClassSessionError}
				studentCount={users.length}
				isMonitoringClass
				inClassroom
			/>
			<ClassroomEditSession
				open={showEditClassModal}
				onExtendError={startClassSessionError}
				durationPlaceholder={classExpiryStr}
				scheduledClass={false}
				isMonitoringClass
				inClassroom
			/>
			<OneActiveMonitorWarningModal
				open={showMonitoringClassActiveWarning}
				onClose={() => useClassroomSessionStore.getState().handleCloseMonitoringWarningModal()}
				existingClassname={activeClassroom.name}
				onStart={(expiry) => {
					useClassroomSessionStore.getState().handleStartClassSession({
						classroomId: classroom.id,
						expiry: expiry,
						force: true,
						monitoringClass: true,
						inClassroom: true,
					});
				}}
			/>
			{p2pModalOpen && renderP2pModal()}
			{totalWindows - shownWindows > 0 || extraWindows > 0 ? (
				<div className="RegularClassroom_showAllWindowsButton" onClick={toggleShowWindows}>
					{showAllWindows ? "Hide (" + extraWindows + ") Extra Screens" : "Show (" + (totalWindows - shownWindows) + ") Extra Screens"}
				</div>
			) : null}
		</div>
	);
};

MonitoringClassroom.propTypes = {
	users: PropTypes.array.isRequired,
	classroom: PropTypes.object,
	isClassStarted: PropTypes.bool,
};

export default MonitoringClassroom;
